import React, { useState, useMemo, useEffect, useReducer } from 'react';
import { Box, Typography, Card, CardContent, useTheme, Stack, Select, MenuItem, FormControl } from '@mui/material';
import { BarChart, BarElementProps } from '@mui/x-charts/BarChart';
import { animated, useSpring, to } from '@react-spring/web';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { apiGet, handleApiError } from '../../../services/apiClient';
import { buildApiUrl, API_ENDPOINTS } from '../../../config/apiEndpoints';

const ANIMATION_DELAY_STEP = 150;


const R = 4;

const topRoundedPath = (x: number, y: number, w: number, h: number, r: number) => {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  const b = y + h;
  return `M ${x} ${b}
          V ${y + rr}
          Q ${x} ${y} ${x + rr} ${y}
          H ${x + w - rr}
          Q ${x + w} ${y} ${x + w} ${y + rr}
          V ${b}
          Z`;
};

function AnimatedBar(props: BarElementProps) {
  const { x = 0, y = 0, width = 0, height = 0, color, dataIndex, style } = props;

  const delay = (dataIndex ?? 0) * ANIMATION_DELAY_STEP;

  const spring = useSpring({
    from: { h: 0, yy: y + height },
    to: { h: height, yy: y },
    delay,
    config: { tension: 120, friction: 14 },
  });

  return (
    <animated.path
      d={to([spring.yy, spring.h], (yy, h) => topRoundedPath(x, yy, width, h, R))}
      fill={color}
      style={style}
    />
  );
}

interface DayData {
  day: string;
  dayLabel: string;
  fullLabel: string;
  unloading: number;
  loading: number;
  isTomorrow?: boolean;
  date: dayjs.Dayjs;
}

interface LoadForecastProps {
  data?: DayData[];
}

const getWeekMonday = (weekSelection: 'current' | 'next' | 'past'): dayjs.Dayjs => {
  const today = dayjs();
  const currentDay = today.day();
  const currentWeekMonday = currentDay === 0
    ? today.add(1, 'day').startOf('day')
    : today.subtract(currentDay - 1, 'day').startOf('day');

  if (weekSelection === 'next') return currentWeekMonday.add(7, 'days');
  if (weekSelection === 'past') return currentWeekMonday.subtract(7, 'days');
  return currentWeekMonday;
};

const LoadForecast: React.FC<LoadForecastProps> = ({ data }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [key, animate] = useReducer((v) => v + 1, 0);
  const [weekSelection, setWeekSelection] = useState<'current' | 'next' | 'past'>('current');
  const [bookings, setBookings] = useState<any[]>([]);
  const [reservations, setReservations] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const monday = getWeekMonday(weekSelection);
        const weekEnd = monday.add(7, 'days');

        const bookingsUrl = buildApiUrl(API_ENDPOINTS.bookings.list);
        const bookingsResponse = await apiGet(bookingsUrl, {
          limit: 1000,
        });
        const bookingsData = await bookingsResponse.json();
        const filteredBookings = (bookingsData.result || []).filter((booking: any) => {
          if (!booking.endTime || !booking.startTime) return false;
          const endDate = dayjs(booking.endTime);
          return (endDate.isAfter(monday) || endDate.isSame(monday, 'day')) &&
            (endDate.isBefore(weekEnd) || endDate.isSame(weekEnd, 'day')) &&
            booking.status === 1;
        });
        setBookings(filteredBookings);

        const reservationsResponse = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/reservations`, {
          limit: 1000,
        });
        const reservationsData = await reservationsResponse.json();
        const filteredReservations = (reservationsData.result || []).filter((reservation: any) => {
          if (!reservation.endTime || !reservation.startTime) return false;
          const endDate = dayjs(reservation.endTime);
          return (endDate.isAfter(monday) || endDate.isSame(monday, 'day')) &&
            (endDate.isBefore(weekEnd) || endDate.isSame(weekEnd, 'day')) &&
            reservation.status === 1;
        });
        setReservations(filteredReservations);
      } catch (error) {
        handleApiError(error, t('Failed to fetch forecast data'));
      }
    };

    fetchData();
  }, [t, weekSelection]);

  const forecastData = useMemo(() => {
    if (data && data.length > 0) {
      return data;
    }

    const days: DayData[] = [];
    const dayLabels = ['M', 'T', 'W', 'Th', 'F', 'S', 'Su'];
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const monday = getWeekMonday(weekSelection);

    for (let i = 0; i < 7; i++) {
      const date = monday.add(i, 'day');
      const isTomorrow = date.isSame(dayjs().add(1, 'day'), 'day');
      const isToday = date.isSame(dayjs(), 'day');

      days.push({
        day: dayLabels[i],
        dayLabel: dayLabels[i],
        fullLabel: isTomorrow ? t('Tomorrow') : isToday ? t('Today') : dayNames[i],
        unloading: 0,
        loading: 0,
        isTomorrow,
        date,
      });
    }

    bookings.forEach((booking) => {
      if (booking.endTime && booking.loadingPlaces) {
        const endDate = dayjs(booking.endTime).startOf('day');
        const dayIndex = days.findIndex(d => d.date.isSame(endDate, 'day'));
        if (dayIndex >= 0) {
          const unloadingPlaces = booking.loadingPlaces.filter(
            (place: any) => place.loadingType === 'UNLOADING'
          );
          days[dayIndex].unloading += unloadingPlaces.length;
        }
      }
      if (booking.startTime && booking.loadingPlaces) {
        const startDate = dayjs(booking.startTime).startOf('day');
        const dayIndex = days.findIndex(d => d.date.isSame(startDate, 'day'));
        if (dayIndex >= 0) {
          const loadingPlaces = booking.loadingPlaces.filter(
            (place: any) => place.loadingType === 'LOADING'
          );
          days[dayIndex].loading += loadingPlaces.length;
        }
      }
    });

    reservations.forEach((reservation) => {
      if (reservation.endTime && reservation.dropoffLocation) {
        const endDate = dayjs(reservation.endTime).startOf('day');
        const dayIndex = days.findIndex(d => d.date.isSame(endDate, 'day'));
        if (dayIndex >= 0) {
          days[dayIndex].unloading += 1;
        }
      }
      if (reservation.startTime && reservation.pickupLocation) {
        const startDate = dayjs(reservation.startTime).startOf('day');
        const dayIndex = days.findIndex(d => d.date.isSame(startDate, 'day'));
        if (dayIndex >= 0) {
          days[dayIndex].loading += 1;
        }
      }
    });

    return days;
  }, [data, bookings, reservations, t, weekSelection]);

  const chartData = useMemo(() => {
    const unloadingData = forecastData.map(d => d.unloading);
    const loadingData = forecastData.map(d => d.loading);
    const xAxisLabels = forecastData.map(d => d.dayLabel);

    return {
      unloadingData,
      loadingData,
      xAxisLabels,
    };
  }, [forecastData]);

  const textColor = theme.palette.mode === 'dark' ? theme.palette.text.white : theme.palette.text.dark;
  const gridColor = theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.3)';

  const unloadingColor = theme.palette.text.purple;
  const loadingColor = theme.palette.mode === 'dark'
    ? 'rgba(139, 92, 246, 0.6)'
    : 'rgba(139, 92, 246, 0.5)';

  useEffect(() => {
    animate();
  }, [forecastData, weekSelection]);


  return (
    <Card
      sx={{
        width: { xs: '100%', md: '40%' },
        flex: { md: '0 0 40%' },
        height: { xs: 'auto', md: '100%' },
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: { xs: 2, sm: 3 },
        border: (theme: any) => `1px solid ${theme.palette.border.default}`,
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.background.white : 'transparent',
        overflow: 'hidden',
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{
        p: 0, flex: 1, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'visible', '&:last-child': { pb: 0 },
        '&.MuiCardContent-root': {
          backgroundColor: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.background.white : 'transparent',
        },
      }}>
        <Box
          sx={{
            px: { xs: 0.75, sm: 1, md: 1.25 },
            py: { xs: 0.5, sm: 0.75, md: 1 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 0.5, sm: 0.75 },
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: textColor,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.125rem' },
              letterSpacing: '0.2px',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {t('Weekly Load Forecast')}
          </Typography>

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              position: { xs: 'relative', sm: 'absolute' },
              left: { sm: '50%' },
              transform: { sm: 'translateX(-50%)' },
              display: { xs: 'none', sm: 'flex' },
              '@media (max-width: 1650px)': {
                display: 'none',
              },
            }}
          >
            <Box
              sx={{
                width: { sm: 8, md: 10 },
                height: { sm: 8, md: 10 },
                borderRadius: '50%',
                bgcolor: unloadingColor,
              }}
            />
            <Typography
              sx={{
                fontSize: { sm: '0.65rem', md: '0.7rem', lg: '0.75rem' },
                color: textColor,
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {t('Unloading')}
            </Typography>
            <Box
              sx={{
                width: { sm: 8, md: 10 },
                height: { sm: 8, md: 10 },
                borderRadius: '50%',
                bgcolor: loadingColor,
              }}
            />
            <Typography
              sx={{
                fontSize: { sm: '0.65rem', md: '0.7rem', lg: '0.75rem' },
                color: textColor,
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {t('Loading')}
            </Typography>
          </Stack>

          <FormControl
            size="small"
            sx={{
              minWidth: { xs: 110, sm: 100, md: 120 },
              ml: { xs: 'auto', sm: 'auto' },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.inputGrey',
                border: 'none',
                fontSize: { xs: '0.75rem', sm: '0.75rem', md: '0.875rem' },
                height: { xs: '36px', sm: '36px' },
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: 'none',
                },
              },
            }}
          >
            <Select
              value={weekSelection}
              onChange={(e) => setWeekSelection(e.target.value as 'current' | 'next' | 'past')}
              displayEmpty
              sx={{
                color: textColor,
                fontSize: { xs: '0.75rem', sm: '0.75rem', md: '0.875rem' },
                '& .MuiSelect-icon': {
                  color: textColor,
                  fontSize: { xs: '1.2rem', sm: '1.2rem' },
                },
              }}
            >
              <MenuItem value="past" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{t('Past week')}</MenuItem>
              <MenuItem value="current" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{t('Current week')}</MenuItem>
              <MenuItem value="next" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{t('Next week')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            px: { xs: 0.5, sm: 0.75, md: 0.75 },
            pt: { xs: 0.25, sm: 0.5, md: 0.5 },
            pb: { xs: 0.25, sm: 0.5, md: 0.5 },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <BarChart
            key={key}
            disableAxisListener
            series={[
              {
                data: chartData.unloadingData,
                label: t('Unloading'),
                stack: 'total',
                color: unloadingColor,

              },
              {
                data: chartData.loadingData,
                label: t('Loading'),
                stack: 'total',
                color: loadingColor,
              },
            ]}
            slots={{
              bar: AnimatedBar,
            }}
            xAxis={[
              {
                scaleType: 'band',
                data: chartData.xAxisLabels,
                categoryGapRatio: 0.3,
                tickLabelStyle: {
                  fill: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.grey,
                  fontSize: 11,
                },
              },
            ]}
            yAxis={[
              {
                tickLabelStyle: {
                  fill: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.grey,
                  fontSize: 11,
                },
              },
            ]}
            grid={{ horizontal: true, vertical: false }}
            slotProps={{
              legend: {
                position: { vertical: 'top', horizontal: 'center' },
              },
            }}
            sx={{
              width: '100%',
              height: '100%',
              flex: 1,
              '& .MuiChartsLegend-root': {
                display: 'none',
              },
              '& .MuiChartsGrid-root': {
                stroke: gridColor,
              },
              '& .MuiChartsAxis-root': {
                stroke: gridColor,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default LoadForecast;
